import {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { ApiDestination } from '../llm-common/llm-types';
import { displayNameToOptionWithDeleted } from 'services/helpers';
import {
  checkDeleted,
  displayNameDescriptor,
} from 'pages/AutomationEngine/helpers';
import {
  DEFAULT_CONFLICT_RESOLUTION,
  DESTINATION_TYPES,
} from '../llm-common/constants';
import { FileExtractionStep } from '../actionSectionTypes';

type ApiObject = {
  inputField: any;
  modelName: string;
  businessPluginApp: any;
  prompt: string;
  htmlPrompt: string;
  mergeFieldValidation: any;
  defaultToValue: any;
  destinations: ApiDestination[];
  isAdvanced: boolean;
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary,
  stepType: any
) => {
  const {
    inputField,
    modelName,
    businessPluginApp,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    defaultToValue,
    destinations,
    isAdvanced,
  } = apiObject;

  checkDeleted(
    messageDictionary,
    addErrorMessage,
    inputField,
    displayNameDescriptor
  );

  if (!modelName || !businessPluginApp) {
    addErrorMessage(messageDictionary.llmPluginAppIsMissing);
  }

  const flow = {
    inputField: displayNameToOptionWithDeleted(
      inputField,
      messageDictionary.deleted
    ),
    modelName: modelName || null,
    businessPluginAppId: businessPluginApp?.id || null,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    defaultToValue,
    destinations: destinations.map(
      ({
        relatedObjectField,
        field,
        variable,
        conflictResolution,
        isRequired,
        confidenceThreshold,
        options,
      }) => {
        checkDeleted(
          messageDictionary,
          addErrorMessage,
          relatedObjectField,
          displayNameDescriptor
        );

        checkDeleted(
          messageDictionary,
          addErrorMessage,
          field,
          displayNameDescriptor
        );

        return {
          field: field
            ? displayNameToOptionWithDeleted(field, messageDictionary.deleted)
            : null,
          relatedObjectField:
            !field?.deleted && relatedObjectField // if field is deleted always return null
              ? displayNameToOptionWithDeleted(
                  relatedObjectField,
                  messageDictionary.deleted
                )
              : null,
          variable: variable
            ? displayNameToOptionWithDeleted(
                { ...variable, displayName: undefined },
                messageDictionary.deleted
              )
            : null,
          conflictResolution: conflictResolution || DEFAULT_CONFLICT_RESOLUTION,
          isRequired: isRequired || false,
          options: (options || []).map((option) =>
            displayNameToOptionWithDeleted(option, messageDictionary.deleted)
          ),
          confidenceThreshold,
          destinationType: relatedObjectField
            ? DESTINATION_TYPES.related_object_field
            : variable
              ? DESTINATION_TYPES.variable
              : DESTINATION_TYPES.this_object_field,
        };
      }
    ),
    isAdvanced,
  };

  return flow;
};

const forApi = (
  step: FileExtractionStep,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const {
    inputField,
    modelName,
    businessPluginAppId,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    destinations,
    isAdvanced,
  } = step.details;

  const api = {
    ...step,
    details: {
      inputField: inputField?.value,
      modelName,
      businessPluginAppId,
      prompt,
      htmlPrompt,
      mergeFieldValidation,
      defaultToValue: 'unknown',
      destinations: destinations.map((destination) => {
        const {
          relatedObjectField,
          field,
          variable,
          conflictResolution = DEFAULT_CONFLICT_RESOLUTION,
          options,
          isRequired,
          confidenceThreshold,
        } = destination;
        return {
          relatedObjectField: relatedObjectField?.value,
          field: field?.value,
          variable: variable?.label,
          conflictResolution,
          options: options.map((option) => option.value),
          isRequired,
          confidenceThreshold,
        };
      }),
      isAdvanced,
    },
  };

  return api;
};

export const fileContentExtractionDTO = {
  forApi,
  forFlow,
};
